import React, { useCallback, useEffect, useState } from 'react';
import {
  Button,
  Checkbox,
  Form,
  Input,
  List,
  message,
  Modal,
  Switch,
  Tag,
} from 'antd';
import { EditOutlined } from '@ant-design/icons';

import api from '../../services/api';
import { CadastroPage } from './CadastroPage';
import { listarClientesV2 } from '../../services/cadastrosV2';

const onlyNumbers = value => (value || '').replace(/\D/g, '');
const formatCnpj = value =>
  onlyNumbers(value).replace(
    /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
    '$1.$2.$3/$4-$5'
  );
const formatPhone = (ddd, phone) => {
  const value = `${ddd || ''}${phone || ''}`;
  return value.length > 10
    ? value.replace(/(\d{2})(\d{5})(\d{4})/, '($1) $2-$3')
    : value.replace(/(\d{2})(\d{4})(\d{4})/, '($1) $2-$3');
};

export function ClientesV2() {
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 350);
    return () => clearTimeout(timer);
  }, [search]);

  useEffect(() => setPage(1), [debouncedSearch]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await listarClientesV2(
        page,
        debouncedSearch,
        undefined,
        onlyActive ? true : undefined
      );
      setItems(response?.data?.items || []);
      setTotal(response?.data?.count || 0);
    } catch (error) {
      setItems([]);
      setTotal(0);
      message.error(
        error?.response?.data?.detail ||
          'Não foi possível carregar os clientes.'
      );
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch, onlyActive]);

  useEffect(() => {
    load();
  }, [load]);

  const openNew = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ ativo: true });
    setOpen(true);
  };

  const openEdit = async item => {
    setOpen(true);
    setSaving(true);
    try {
      const response = await api.get('/Cliente', { params: { id: item.id } });
      const customer = response?.data || item;
      const contact = customer?.contatos?.[0] || item?.contatos?.[0] || {};
      setEditing(customer);
      form.setFieldsValue({
        nomeFantasia: customer.nomeFantasia,
        razaoSocial: customer.razaoSocial,
        cnpj: formatCnpj(customer.cnpj),
        email: contact.email,
        telefone: formatPhone(contact.ddd, contact.telefone),
        ativo: customer.ativo,
      });
    } catch (error) {
      message.error('Não foi possível carregar os dados do cliente.');
      setOpen(false);
    } finally {
      setSaving(false);
    }
  };

  const close = () => {
    setOpen(false);
    setEditing(null);
    form.resetFields();
  };

  const save = async values => {
    setSaving(true);
    const phone = onlyNumbers(values.telefone);
    const currentContact = editing?.contatos?.[0] || {};
    const payload = {
      id: editing?.id,
      cnpj: onlyNumbers(values.cnpj),
      nomeFantasia: values.nomeFantasia.trim(),
      razaoSocial: values.razaoSocial.trim(),
      ativo: values.ativo,
      dataCadastro: editing?.dataCadastro || new Date(),
      contatos: [
        {
          ...currentContact,
          nome: values.nomeFantasia.trim(),
          email: values.email.trim(),
          ddd: Number(phone.slice(0, 2)),
          telefone: Number(phone.slice(2)),
          ativo: values.ativo,
        },
      ],
      enderecos: editing?.enderecos || [],
      parametros: editing?.parametros,
    };

    try {
      if (editing) await api.put('/Cliente', payload);
      else await api.post('/Cliente', payload);
      message.success(
        `Cliente ${editing ? 'atualizado' : 'cadastrado'} com sucesso.`
      );
      close();
      await load();
    } catch (error) {
      message.error(
        error?.response?.data?.detail || 'Não foi possível salvar o cliente.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <CadastroPage
        title="Clientes"
        newLabel="Novo cliente"
        search={search}
        onSearch={setSearch}
        loading={loading}
        items={items}
        total={total}
        page={page}
        onPageChange={setPage}
        onNew={openNew}
        extraFilters={
          <Checkbox
            checked={onlyActive}
            onChange={event => {
              setOnlyActive(event.target.checked);
              setPage(1);
            }}
          >
            Somente ativos
          </Checkbox>
        }
        renderItem={item => (
          <List.Item
            key={item.id}
            actions={[
              <Button
                key="edit"
                type="link"
                icon={<EditOutlined />}
                onClick={() => openEdit(item)}
              >
                Editar
              </Button>,
            ]}
          >
            <List.Item.Meta
              title={item.nomeFantasia}
              description={`${formatCnpj(item.cnpj)} • ${item.razaoSocial ||
                'Sem razão social'}`}
            />
            <Tag color={item.ativo ? 'green' : 'default'}>
              {item.ativo ? 'Ativo' : 'Inativo'}
            </Tag>
          </List.Item>
        )}
      />

      <Modal
        open={open}
        title={editing ? 'Editar cliente' : 'Novo cliente'}
        footer={null}
        width={680}
        maskClosable={false}
        destroyOnHidden
        onCancel={close}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={save}
          initialValues={{ ativo: true }}
        >
          <Form.Item
            name="nomeFantasia"
            label="Nome fantasia"
            rules={[{ required: true, message: 'Informe o nome fantasia.' }]}
          >
            <Input maxLength={150} />
          </Form.Item>
          <Form.Item
            name="razaoSocial"
            label="Razão social"
            rules={[{ required: true, message: 'Informe a razão social.' }]}
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            name="cnpj"
            label="CNPJ"
            rules={[
              { required: true, message: 'Informe o CNPJ.' },
              {
                validator: (_, value) =>
                  onlyNumbers(value).length === 14
                    ? Promise.resolve()
                    : Promise.reject(
                        new Error('Informe os 14 dígitos do CNPJ.')
                      ),
              },
            ]}
          >
            <Input maxLength={18} />
          </Form.Item>
          <Form.Item
            name="email"
            label="E-mail"
            rules={[
              {
                required: true,
                type: 'email',
                message: 'Informe um e-mail válido.',
              },
            ]}
          >
            <Input maxLength={200} />
          </Form.Item>
          <Form.Item
            name="telefone"
            label="Telefone"
            rules={[{ required: true, message: 'Informe o telefone.' }]}
          >
            <Input maxLength={16} />
          </Form.Item>
          <Form.Item name="ativo" label="Status" valuePropName="checked">
            <Switch checkedChildren="Ativo" unCheckedChildren="Inativo" />
          </Form.Item>
          <div className="cadastros-v2-modal-actions">
            <Button onClick={close}>Cancelar</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Salvar
            </Button>
          </div>
        </Form>
      </Modal>
    </>
  );
}
